import React from "react";
import { Search } from "lucide-react";

import { Input } from "@/components/ui/input";
import { isCaseConversation } from "../utils/caseConversation";
import { formatRelativeTime, parseTimestamp } from "../utils/time";
import { usePlayground } from "../hooks/usePlayground";
import { ConversationSearchDialog } from "./ConversationSearchDialog";
import { ConversationListItem } from "./ConversationListItem";

type PlaygroundConversationRef = {
  playground_conversation_id: string;
  title?: string | null;
  scope?: string | null;
  last_message_at?: unknown;
};

type Props = {
  conversations: PlaygroundConversationRef[];
  // Support both prop names (some call sites may still use the older name).
  selectedConversationId?: string | null;
  activeConversationId?: string | null;
  onRenameConversation?: (playgroundConversationId: string) => void;
  onDeleteConversation?: (playgroundConversationId: string) => void;
  disableConversationActions?: boolean;
};

function getConversationTitle(c: PlaygroundConversationRef): string {
  return (c.title ?? "").trim() || "Untitled conversation";
}

function getConversationSubtitle(c: PlaygroundConversationRef): string {
  const date = parseTimestamp(c.last_message_at);
  const when = date ? formatRelativeTime(date) : "—";
  return isCaseConversation(c) ? `Case · ${when}` : when;
}

export function ConversationsSidebar({
  conversations,
  selectedConversationId,
  activeConversationId,
  onRenameConversation,
  onDeleteConversation,
  disableConversationActions = false
}: Props) {
  const { selectConversation } = usePlayground();
  const effectiveSelectedId =
    selectedConversationId ?? activeConversationId ?? null;

  const [isSearchOpen, setIsSearchOpen] = React.useState(false);

  return (
    <div className="flex w-72 flex-none flex-col items-start gap-4 self-stretch overflow-auto border-r border-line-2 bg-chrome p-3 mobile:hidden">
      <div className="flex w-full flex-col items-start gap-3">
        <div className="flex flex-col items-start gap-0.5 px-1 pt-1">
          <span className="text-base font-medium text-fg">Conversations</span>
          <span className="text-sm text-fg-3">
            Read-only. Start new research from a case&apos;s chat.
          </span>
        </div>

        <div className="relative w-full flex-none">
          <Search
            className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-fg-3"
            aria-hidden
          />
          <Input
            readOnly
            placeholder="Search conversations..."
            value=""
            onClick={() => setIsSearchOpen(true)}
            onFocus={() => setIsSearchOpen(true)}
            onChange={() => {}}
            className="cursor-pointer bg-elevated pl-7"
          />
        </div>
      </div>

      <div className="flex w-full flex-col items-start gap-0.5">
        <ConversationSearchDialog
          open={isSearchOpen}
          onOpenChange={setIsSearchOpen}
          conversations={conversations}
          selectedConversationId={effectiveSelectedId}
          onSelectConversation={(playgroundConversationId) => {
            selectConversation(playgroundConversationId).catch(() => {});
          }}
        />

        {conversations.map((c) => (
          <ConversationListItem
            key={c.playground_conversation_id}
            playgroundConversationId={c.playground_conversation_id}
            selected={c.playground_conversation_id === effectiveSelectedId}
            title={getConversationTitle(c)}
            subtitle={getConversationSubtitle(c)}
            onClick={() => {
              selectConversation(c.playground_conversation_id).catch(() => {});
            }}
            onRename={isCaseConversation(c) ? undefined : onRenameConversation}
            onDelete={isCaseConversation(c) ? undefined : onDeleteConversation}
            disableActions={disableConversationActions}
          />
        ))}
      </div>
    </div>
  );
}
